import React, { useState, useEffect } from 'react';

import { useParams, useSearchParams } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL || 'https://ivision-crm-backend-production.up.railway.app';

function PublicBooking() {
  const { subdomain } = useParams();
  const [searchParams] = useSearchParams();
  const preselectedStaff = searchParams.get("staff");
  const [step, setStep] = useState(1); // 1=service, 2=staff, 3=date/time, 4=info, 5=confirm
  const [business, setBusiness] = useState(null);
  const [services, setServices] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [serviceSearch, setServiceSearch] = useState('');
  const [openCategory, setOpenCategory] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [slots, setSlots] = useState([]);
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [confirmation, setConfirmation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const accent = business?.secondary_color || business?.primary_color || '#c9a96e';
  const isBarbershop = business?.business_type === 'barbershop' || business?.business_type === 'barber';
  const isSpa = business?.business_type === 'spa' || business?.business_type === 'massage';
  const headingFont = isBarbershop ? "'Bebas Neue', sans-serif" : isSpa ? "'Cormorant Garamond', serif" : "'DM Sans', sans-serif";
  const bodyFont = isBarbershop ? "'Inter', sans-serif" : isSpa ? "'Jost', sans-serif" : "'DM Sans', sans-serif";
  const pageBg = isBarbershop ? '#0a0a0a' : '#faf6ef';
  const headerBg = isBarbershop ? 'linear-gradient(135deg, #0a0a0a, #1a1a1a)' : 'linear-gradient(135deg, #1a1714, #2d2924)';
  const cardBg = isBarbershop ? '#141414' : '#fff';
  const textColor = isBarbershop ? '#f0f0f0' : '#1a1714';
  const mutedColor = isBarbershop ? '#888' : 'rgba(0,0,0,0.5)';
  const borderColor = isBarbershop ? 'rgba(200,164,94,0.2)' : 'rgba(201,169,110,0.25)';
  useEffect(() => {
    const fl = document.createElement('link');
    if (isBarbershop) {
      fl.href = 'https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Inter:wght@300;400;500;600;700&display=swap';
    } else {
      fl.href = 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600;700&family=Jost:wght@300;400;500;600&display=swap';
    }
    fl.rel = 'stylesheet';
    document.head.appendChild(fl);
  }, [isBarbershop]);
  const accent2 = business?.primary_color || '#8b6f47';

  useEffect(() => {
    loadBusiness();
  }, [subdomain]);

  const loadBusiness = async () => {
    try {
      const res = await fetch(`${API}/api/public/book/${subdomain}`);
      const data = await res.json();
      if (data.success) {
        setBusiness(data.business);
        setServices(data.services);
        setStaffList(data.staff);
        // Auto-select staff from URL param (?staff=ID or ?staff=first-last)
        if (preselectedStaff) {
          const match = data.staff.find(s => String(s.id) === preselectedStaff || (s.first_name + "-" + s.last_name).toLowerCase() === preselectedStaff.toLowerCase());
          if (match) { setSelectedStaff(match); setStep(1); }
        }
      } else {
        setError('Business not found');
      }
    } catch (e) {
      setError('Unable to connect');
    }
    setLoading(false);
  };

  const loadSlots = async (date, serviceId, staffId) => {
    setSlotsLoading(true);
    setSlots([]);
    try {
      const params = new URLSearchParams({ date, serviceId, staffId: staffId || 'any' });
      const res = await fetch(`${API}/api/public/book/${subdomain}/slots?${params}`);
      const data = await res.json();
      if (data.success) setSlots(data.slots);
    } catch (e) { console.error(e); }
    setSlotsLoading(false);
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setSelectedSlot(null);
    if (date && selectedService) {
      loadSlots(date, selectedService.id, selectedStaff?.id);
    }
  };

  const handleSubmit = async () => {
    if (!customerName || !customerPhone) {
      setError('Name and phone are required');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const staffId = selectedSlot.selectedStaffId || selectedStaff?.id || selectedSlot.availableStaff[0]?.id;
      const res = await fetch(`${API}/api/public/book/${subdomain}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          serviceId: selectedService.id,
          staffId,
          date: selectedDate,
          time: selectedSlot.time,
          customerName,
          customerPhone,
          customerEmail,
          notes,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setConfirmation(data.booking);
        setStep(5);
      } else {
        setError(data.error || 'Booking failed');
      }
    } catch (e) {
      setError('Connection error');
    }
    setSubmitting(false);
  };

  // Generate next 14 days
  const getDateOptions = () => {
    const dates = [];
    const today = new Date();
    for (let i = 0; i < 14; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      dates.push({
        value: d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0'),
        label: d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
        day: d.toLocaleDateString('en-US', { weekday: 'short' }),
        date: d.getDate(),
        month: d.toLocaleDateString('en-US', { month: 'short' }),
      });
    }
    return dates;
  };

  const inputStyle = {
    width: '100%', padding: '14px 18px', background: '#fff', border: '1px solid rgba(201,169,110,0.25)',
    borderRadius: '12px', fontSize: '15px', color: '#1a1714', outline: 'none', fontFamily: bodyFont,
    transition: 'border-color 0.2s',
  };
  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: pageBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 40, height: 40, margin: '0 auto 16px',
            border: '3px solid #e8e8f0',
            borderTopColor: accent,
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <p style={{ color: '#888', fontSize: '14px', fontWeight: 500 }}>Loading...</p>
        </div>
      </div>
    );
  }
  if (error && !business) {
    return (
      <div style={{ minHeight: '100vh', background: pageBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>😕</div>
          <h2 style={{ color: '#333', marginBottom: '8px' }}>Business Not Found</h2>
          <p style={{ color: '#888' }}>This booking link may be incorrect or inactive.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: pageBg }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet" />

      {/* Header */}
      <div style={{ background: `linear-gradient(135deg, #1a1714, #2d2924)`, padding: '32px 24px', textAlign: 'center' }}>
        <h1 style={{ color: '#c9a96e', fontSize: '28px', fontWeight: '500', margin: '0 0 4px', fontFamily: headingFont, letterSpacing: '1px' }}>
          {business?.name || 'Book Now'}
        </h1>
        <p style={{ color: isBarbershop ? 'rgba(200,164,94,0.6)' : 'rgba(201,169,110,0.6)', fontSize: '14px', margin: 0, fontFamily: bodyFont }}>
          {business?.address && `${business.address}, ${business.city}`}
          {business?.phone && ` · ${business.phone}`}
        </p>
      </div>

      {/* Progress */}
      {step < 5 && (
        <div style={{ background: pageBg, padding: '16px 24px', borderBottom: '1px solid rgba(201,169,110,0.2)', display: 'flex', justifyContent: 'center', gap: '8px' }}>
          {['Service', 'Provider', 'Date & Time', 'Your Info'].map((s, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                width: '28px', height: '28px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '12px', fontWeight: '700',
                background: step > i + 1 ? accent : step === i + 1 ? accent : '#e8e8ee',
                color: step >= i + 1 ? '#fff' : '#aaa',
              }}>{step > i + 1 ? '✓' : i + 1}</div>
              <span style={{ fontSize: '13px', fontWeight: '600', color: step === i + 1 ? '#333' : '#aaa', display: i < 3 ? 'none' : 'none' }}>{s}</span>
              {i < 3 && <div style={{ width: '24px', height: '2px', background: step > i + 1 ? accent : '#e8e8ee', borderRadius: '1px' }} />}
            </div>
          ))}
        </div>
      )}

      <div style={{ maxWidth: '520px', margin: '0 auto', padding: '24px', fontFamily: bodyFont }}>
        {error && step < 5 && (
          <div style={{ background: '#fff0f0', border: '1px solid #ffdada', borderRadius: '12px', padding: '12px 16px', marginBottom: '20px', color: '#d63031', fontSize: '14px', fontWeight: '500' }}>
            {error}
          </div>
        )}

        {/* Step 1: Select Service */}
        {step === 1 && (() => {
          // Group services by category
          var grouped = {};
          var uncategorized = [];
          services.forEach(s => {
            if (s.category) {
              if (!grouped[s.category]) grouped[s.category] = [];
              grouped[s.category].push(s);
            } else {
              uncategorized.push(s);
            }
          });
          var categoryOrder = Object.keys(grouped).sort();
          var hasCategories = categoryOrder.length > 0;

          // Search filter
          var searchLower = (serviceSearch || '').toLowerCase();
          var filterService = function(s) {
            if (!searchLower) return true;
            return s.name.toLowerCase().includes(searchLower) ||
                   (s.description || '').toLowerCase().includes(searchLower) ||
                   (s.category || '').toLowerCase().includes(searchLower);
          };

          var renderCard = function(s) {
            return (
              <div key={s.id} onClick={() => { setSelectedService(s); setStep(2); setError(''); }}
                style={{
                  background: '#fff', border: '1px solid',
                  borderColor: selectedService?.id === s.id ? accent : '#e8e8ee',
                  borderRadius: '14px', padding: '16px 20px',
                  cursor: 'pointer', transition: 'all 0.2s',
                }}
                onMouseEnter={e => e.currentTarget.style.borderColor = accent}
                onMouseLeave={e => { if (selectedService?.id !== s.id) e.currentTarget.style.borderColor = '#e8e8ee'; }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#333', margin: '0 0 2px' }}>{s.name}</h3>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginTop: 2 }}>
                      <span style={{ fontSize: '12px', color: '#999', background: '#f5f5f7', padding: '2px 8px', borderRadius: 6 }}>⏱ {s.duration} min</span>
                      {s.description && <span style={{ fontSize: '12px', color: '#aaa' }}>{s.description.length > 60 ? s.description.slice(0,60) + '…' : s.description}</span>}
                    </div>
                  </div>
                  <span style={{ fontSize: '18px', fontWeight: '800', color: accent, marginLeft: 16, whiteSpace: 'nowrap' }}>${parseFloat(s.price).toFixed(0)}</span>
                </div>
              </div>
            );
          };

          return (
            <div>
              {staffList.length > 1 && isBarbershop && (
              <div style={{ marginBottom: '24px' }}>
                <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#333', marginBottom: '4px' }}>Book with a Barber</h2>
                <p style={{ fontSize: '14px', color: '#888', marginBottom: '14px' }}>Choose your barber, or scroll down to pick a service first</p>
                <div style={{ display: 'flex', gap: '10px', overflowX: 'auto', paddingBottom: '8px' }}>
                  {staffList.map(s => (
                    <div key={s.id} onClick={() => { setSelectedStaff(s); setStep(2); }}
                      style={{ minWidth: '120px', background: '#fff', border: '2px solid #e8e8ee', borderRadius: '16px', padding: '16px', cursor: 'pointer', textAlign: 'center', transition: 'all 0.2s' }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = accent}
                      onMouseLeave={e => e.currentTarget.style.borderColor = '#e8e8ee'}
                    >
                      <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: `linear-gradient(135deg, ${accent}20, ${accent}40)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', fontWeight: '700', color: accent, margin: '0 auto 8px' }}>{s.first_name.charAt(0)}</div>
                      <div style={{ fontSize: '14px', fontWeight: '700', color: '#333' }}>{s.first_name}</div>
                      <div style={{ fontSize: '11px', color: '#999', marginTop: '2px' }}>{s.role || 'Barber'}</div>
                    </div>
                  ))}
                </div>
                <div style={{ borderBottom: '1px solid #eee', margin: '16px 0' }} />
              </div>
            )}
              <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#333', marginBottom: '4px' }}>Choose a Service</h2>
              <p style={{ fontSize: '14px', color: '#888', marginBottom: '16px' }}>Select the service you'd like to book</p>

              {/* Search */}
              {services.length > 6 && (
                <input
                  placeholder="🔍 Search services..."
                  value={serviceSearch || ''}
                  onChange={e => setServiceSearch(e.target.value)}
                  style={{
                    width: '100%', padding: '12px 16px', border: '2px solid #e8e8ee', borderRadius: 12,
                    fontSize: 14, outline: 'none', marginBottom: 16, boxSizing: 'border-box',
                    background: '#fafafa',
                  }}
                />
              )}

              {services.length === 0 ? (
                <p style={{ textAlign: 'center', color: '#888', padding: '40px' }}>No services available for online booking</p>
              ) : hasCategories ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {categoryOrder.map((cat, ci) => {
                    var catServices = grouped[cat].filter(filterService);
                    if (catServices.length === 0) return null;
                    var isOpen = openCategory === cat || !!searchLower;
                    var catColors = [
                      { bg: accent, text: '#fff' },
                      { bg: '#1e293b', text: '#fff' },
                      { bg: '#7c3aed', text: '#fff' },
                      { bg: '#0891b2', text: '#fff' },
                      { bg: '#c2410c', text: '#fff' },
                      { bg: '#15803d', text: '#fff' },
                    ];
                    var cc = catColors[ci % catColors.length];
                    return (
                      <div key={cat} style={{ border: '2px solid #e8e8ee', borderRadius: 14, overflow: 'hidden', transition: 'all 0.2s' }}>
                        <div onClick={() => setOpenCategory(openCategory === cat ? null : cat)}
                          style={{
                            padding: '16px 20px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            background: cc.bg, transition: 'opacity 0.2s',
                          }}
                          onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
                          onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                        >
                          <div>
                            <div style={{ fontSize: 16, fontWeight: 800, color: cc.text, letterSpacing: '0.5px' }}>{cat}</div>
                            <div style={{ fontSize: 12, color: cc.text, opacity: 0.8, marginTop: 3 }}>{catServices.length} service{catServices.length !== 1 ? 's' : ''} · from ${Math.min(...catServices.map(s => parseFloat(s.price))).toFixed(0)}</div>
                          </div>
                          <span style={{ fontSize: 18, color: cc.text, opacity: 0.7, transition: 'transform 0.2s', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>▾</span>
                        </div>
                        {isOpen && (
                          <div style={{ padding: '0 12px 12px', display: 'flex', flexDirection: 'column', gap: 6, borderTop: '1px solid #f0f0f5' }}>
                            {catServices.map(renderCard)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {uncategorized.filter(filterService).length > 0 && (() => {
                    var uncatFiltered = uncategorized.filter(filterService);
                    var isOpen = openCategory === '__other' || !!searchLower;
                    return (
                      <div style={{ border: '2px solid #e8e8ee', borderRadius: 14, overflow: 'hidden' }}>
                        <div onClick={() => setOpenCategory(openCategory === '__other' ? null : '__other')}
                          style={{
                            padding: '16px 20px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            background: '#6b7280', transition: 'opacity 0.2s',
                          }}
                          onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
                          onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                        >
                          <div>
                            <div style={{ fontSize: 16, fontWeight: 800, color: '#fff', letterSpacing: '0.5px' }}>Other Services</div>
                            <div style={{ fontSize: 12, color: '#fff', opacity: 0.8, marginTop: 3 }}>{uncatFiltered.length} service{uncatFiltered.length !== 1 ? 's' : ''}</div>
                          </div>
                          <span style={{ fontSize: 18, color: '#fff', opacity: 0.7, transition: 'transform 0.2s', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>▾</span>
                        </div>
                        {isOpen && (
                          <div style={{ padding: '0 12px 12px', display: 'flex', flexDirection: 'column', gap: 6, borderTop: '1px solid #f0f0f5' }}>
                            {uncatFiltered.map(renderCard)}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {services.filter(filterService).map(renderCard)}
                </div>
              )}
            </div>
          );
        })()}

        {/* Step 2: Select Staff */}
        {step === 2 && (() => {
          // Auto-skip if 0 or 1 staff
          if (staffList.length <= 1) {
            setTimeout(() => { setSelectedStaff(staffList[0] || null); setStep(3); }, 0);
            return <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>Loading...</div>;
          }
          return (
          <div>
            <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#333', marginBottom: '4px' }}>Choose a Provider</h2>
            <p style={{ fontSize: '14px', color: '#888', marginBottom: '20px' }}>Who would you like to see?</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div onClick={() => { setSelectedStaff(null); setStep(3); setError(''); }}
                style={{ background: '#fff', border: '2px solid #e8e8ee', borderRadius: '16px', padding: '20px', cursor: 'pointer', transition: 'all 0.2s' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = accent}
                onMouseLeave={e => e.currentTarget.style.borderColor = '#e8e8ee'}
              >
                <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#333', margin: 0 }}>🎲 Any Available</h3>
                <p style={{ fontSize: '13px', color: '#888', margin: '4px 0 0' }}>First available provider</p>
              </div>
              {staffList.map(s => (
                <div key={s.id} onClick={() => { setSelectedStaff(s); setStep(3); setError(''); }}
                  style={{ background: '#fff', border: '2px solid #e8e8ee', borderRadius: '16px', padding: '20px', cursor: 'pointer', transition: 'all 0.2s' }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = accent}
                  onMouseLeave={e => e.currentTarget.style.borderColor = '#e8e8ee'}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                    <div style={{
                      width: '44px', height: '44px', borderRadius: '50%',
                      background: `linear-gradient(135deg, ${accent}20, ${accent2}20)`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '18px', fontWeight: '700', color: accent,
                    }}>{s.first_name.charAt(0)}</div>
                    <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#333', margin: 0 }}>{s.first_name} {s.last_name || ''}</h3>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={() => setStep(1)} style={{ marginTop: '16px', background: 'none', border: 'none', color: '#888', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>← Back</button>
          </div>
          );
        })()}

        {/* Step 3: Date & Time */}
        {step === 3 && (
          <div>
            <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#333', marginBottom: '4px' }}>Pick a Date & Time</h2>
            <p style={{ fontSize: '14px', color: '#888', marginBottom: '20px' }}>
              {selectedService?.name} · {selectedService?.duration} min · ${parseFloat(selectedService?.price).toFixed(0)}
            </p>

            {/* Date selector */}
            <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '12px', marginBottom: '20px' }}>
              {getDateOptions().map(d => (
                <div key={d.value} onClick={() => handleDateChange(d.value)}
                  style={{
                    minWidth: '64px', padding: '12px 8px', borderRadius: '14px', textAlign: 'center', cursor: 'pointer',
                    background: selectedDate === d.value ? accent : '#fff',
                    border: `2px solid ${selectedDate === d.value ? accent : '#e8e8ee'}`,
                    transition: 'all 0.2s',
                  }}>
                  <div style={{ fontSize: '11px', fontWeight: '600', color: selectedDate === d.value ? 'rgba(255,255,255,0.7)' : '#aaa', marginBottom: '2px' }}>{d.day}</div>
                  <div style={{ fontSize: '20px', fontWeight: '800', color: selectedDate === d.value ? '#fff' : '#333' }}>{d.date}</div>
                  <div style={{ fontSize: '11px', fontWeight: '600', color: selectedDate === d.value ? 'rgba(255,255,255,0.7)' : '#aaa' }}>{d.month}</div>
                </div>
              ))}
            </div>

            {/* Time slots */}
            {selectedDate && (
              slotsLoading ? (
                <p style={{ textAlign: 'center', color: '#888', padding: '24px' }}>Finding available times...</p>
              ) : slots.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '32px', background: '#fff', borderRadius: '16px', border: '1px solid #eee' }}>
                  <p style={{ fontSize: '28px', marginBottom: '8px' }}>😔</p>
                  <p style={{ color: '#888', fontSize: '14px' }}>No available times on this date. Try another day.</p>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                  {slots.map((slot, i) => (
                    <button key={i} onClick={() => { setSelectedSlot(slot); setStep(4); setError(''); }}
                      style={{
                        padding: '14px 8px', borderRadius: '12px', fontSize: '14px', fontWeight: '600', cursor: 'pointer',
                        border: `2px solid ${selectedSlot?.time === slot.time ? accent : '#e8e8ee'}`,
                        background: selectedSlot?.time === slot.time ? accent : '#fff',
                        color: selectedSlot?.time === slot.time ? '#fff' : '#333',
                        transition: 'all 0.2s',
                      }}>
                      {slot.display}
                    </button>
                  ))}
                </div>
              )
            )}
            <button onClick={() => setStep(2)} style={{ marginTop: '16px', background: 'none', border: 'none', color: '#888', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>← Back</button>
          </div>
        )}

        {/* Step 4: Customer Info */}
        {step === 4 && (
          <div>
            <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#333', marginBottom: '4px' }}>Your Information</h2>
            <p style={{ fontSize: '14px', color: '#888', marginBottom: '20px' }}>Almost done! We just need a few details.</p>

            {/* Summary */}
            <div style={{ background: '#fff', border: '2px solid #e8e8ee', borderRadius: '16px', padding: '20px', marginBottom: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '14px', color: '#888' }}>Service</span>
                <span style={{ fontSize: '14px', fontWeight: '700', color: '#333' }}>{selectedService?.name}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '14px', color: '#888' }}>Date</span>
                <span style={{ fontSize: '14px', fontWeight: '700', color: '#333' }}>{new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '14px', color: '#888' }}>Time</span>
                <span style={{ fontSize: '14px', fontWeight: '700', color: '#333' }}>{selectedSlot?.display}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '14px', color: '#888' }}>Price</span>
                <span style={{ fontSize: '16px', fontWeight: '800', color: accent }}>${parseFloat(selectedService?.price).toFixed(0)}</span>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '24px' }}>
              <div>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#666', marginBottom: '6px', display: 'block' }}>Full Name *</label>
                <input value={customerName} onChange={e => setCustomerName(e.target.value)} placeholder="Your name" style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#666', marginBottom: '6px', display: 'block' }}>Phone *</label>
                <input value={customerPhone} onChange={e => setCustomerPhone(e.target.value)} placeholder="(702) 555-0123" type="tel" style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#666', marginBottom: '6px', display: 'block' }}>Email (optional)</label>
                <input value={customerEmail} onChange={e => setCustomerEmail(e.target.value)} placeholder="email@example.com" type="email" style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#666', marginBottom: '6px', display: 'block' }}>Notes (optional)</label>
                <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any special requests..." rows="2" style={{ ...inputStyle, resize: 'vertical' }} />
              </div>
            </div>

            <button onClick={handleSubmit} disabled={submitting}
              style={{
                width: '100%', padding: '18px', borderRadius: '14px', border: 'none',
                background: `linear-gradient(135deg, #1a1714, #2d2924)`, color: '#fff',
                fontSize: '16px', fontWeight: '800', cursor: 'pointer',
                opacity: submitting ? 0.7 : 1, transition: 'all 0.2s',
              }}>
              {submitting ? 'Booking...' : 'Confirm Booking'}
            </button>
            <button onClick={() => setStep(3)} style={{ width: '100%', marginTop: '12px', padding: '12px', background: 'none', border: 'none', color: '#888', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>← Back</button>
          </div>
        )}

        {/* Step 5: Confirmation */}
        {step === 5 && confirmation && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <div style={{ fontSize: '60px', marginBottom: '16px' }}>📋</div>
            <h2 style={{ fontSize: '24px', fontWeight: '800', color: '#333', marginBottom: '8px' }}>Request Submitted!</h2>
            <p style={{ fontSize: '15px', color: '#888', marginBottom: '32px' }}>We'll review your request and send you a confirmation text shortly.</p>

            <div style={{ background: '#fff', border: '2px solid #e8e8ee', borderRadius: '20px', padding: '28px', textAlign: 'left', marginBottom: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '14px', paddingBottom: '14px', borderBottom: '1px solid #f0f0f0' }}>
                <span style={{ color: '#888', fontSize: '14px' }}>Service</span>
                <span style={{ fontWeight: '700', color: '#333', fontSize: '14px' }}>{confirmation.service}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '14px', paddingBottom: '14px', borderBottom: '1px solid #f0f0f0' }}>
                <span style={{ color: '#888', fontSize: '14px' }}>Date</span>
                <span style={{ fontWeight: '700', color: '#333', fontSize: '14px' }}>{confirmation.date}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '14px', paddingBottom: '14px', borderBottom: '1px solid #f0f0f0' }}>
                <span style={{ color: '#888', fontSize: '14px' }}>Time</span>
                <span style={{ fontWeight: '700', color: '#333', fontSize: '14px' }}>{confirmation.time}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#888', fontSize: '14px' }}>Price</span>
                <span style={{ fontWeight: '800', color: accent, fontSize: '18px' }}>${parseFloat(confirmation.price).toFixed(0)}</span>
              </div>
            </div>

            <p style={{ fontSize: '13px', color: '#aaa' }}>Confirmation #{confirmation.id} · We'll send you a reminder before your appointment.</p>

            <button onClick={() => { setStep(1); setSelectedService(null); setSelectedStaff(null); setSelectedDate(''); setSelectedSlot(null); setCustomerName(''); setCustomerPhone(''); setCustomerEmail(''); setNotes(''); setConfirmation(null); }}
              style={{
                marginTop: '24px', padding: '14px 32px', borderRadius: '12px', border: `2px solid ${accent}`,
                background: 'transparent', color: accent, fontSize: '15px', fontWeight: '700', cursor: 'pointer',
              }}>
              Book Another Appointment
            </button>
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{ textAlign: 'center', padding: '32px 24px', borderTop: '1px solid #eee', marginTop: '40px' }}>
        <p style={{ fontSize: '12px', color: '#bbb' }}>Powered by <span style={{ fontWeight: '700', color: '#999' }}>IVision Marketing</span></p>
      </div>
    </div>
  );
}

export default PublicBooking;
